import { useEffect, useState } from "react"
import { api, ApiError, type ModerationConfigData } from "./api"

export function SettingsView() {
    const [config, setConfig] = useState<ModerationConfigData | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [saved, setSaved] = useState(false)
    const [busy, setBusy] = useState(false)

    useEffect(() => {
        api.getConfig()
            .then(setConfig)
            .catch((err) => setError(err instanceof ApiError ? err.message : "Failed to load settings"))
    }, [])

    if (error && !config) return <p className="error">{error}</p>
    if (!config) return <p>Loading…</p>

    function update(patch: Partial<ModerationConfigData>) {
        setConfig((prev) => (prev ? { ...prev, ...patch } : prev))
        setSaved(false)
    }

    async function handleSave() {
        if (!config) return
        setBusy(true)
        setError(null)
        try {
            const updated = await api.updateConfig({
                healEverything: config.healEverything,
                patchThreshold: config.patchThreshold,
                reviewThreshold: config.reviewThreshold,
                rejectThreshold: config.rejectThreshold,
            })
            setConfig(updated)
            setSaved(true)
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Failed to save settings")
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="settings-view">
            <section className="submit-panel">
                <h2>Moderation behavior</h2>

                <label className="toggle-row">
                    <input
                        type="checkbox"
                        checked={config.healEverything}
                        onChange={(e) => update({ healEverything: e.currentTarget.checked })}
                    />
                    <div>
                        <strong>Heal everything</strong>
                        <p className="muted">
                            When on, no video is ever rejected or held for review — every detected
                            violation is redacted (blurred/pixelated/muted) and the video is published,
                            regardless of risk score. The score is still computed and shown for
                            reporting.
                        </p>
                    </div>
                </label>

                <div className={config.healEverything ? "thresholds disabled" : "thresholds"}>
                    <h3>Risk thresholds</h3>
                    <p className="muted">
                        Score below <em>patch</em> → approve as-is · at or above <em>patch</em> → auto-redact ·
                        at or above <em>review</em> → hold for a human · at or above <em>reject</em> → reject.
                        {config.healEverything && " Ignored while Heal everything is on."}
                    </p>
                    <div className="threshold-inputs">
                        <label>
                            Patch ≥
                            <input
                                type="number"
                                min={0}
                                max={100}
                                value={config.patchThreshold}
                                onChange={(e) => update({ patchThreshold: Number(e.currentTarget.value) })}
                            />
                        </label>
                        <label>
                            Review ≥
                            <input
                                type="number"
                                min={0}
                                max={100}
                                value={config.reviewThreshold}
                                onChange={(e) => update({ reviewThreshold: Number(e.currentTarget.value) })}
                            />
                        </label>
                        <label>
                            Reject ≥
                            <input
                                type="number"
                                min={0}
                                max={100}
                                value={config.rejectThreshold}
                                onChange={(e) => update({ rejectThreshold: Number(e.currentTarget.value) })}
                            />
                        </label>
                    </div>
                </div>

                <div className="settings-actions">
                    <button onClick={handleSave} disabled={busy}>
                        {busy ? "Saving…" : "Save settings"}
                    </button>
                    {saved && <span className="saved-note">Saved — applies to all new jobs immediately.</span>}
                    {error && <span className="error">{error}</span>}
                </div>
            </section>
        </div>
    )
}
